class AnnouncementBar extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = -1;
    this.autoSlideTimer = null;
    this.isScrolling = false;
  }

  get duration() {
    return Number(this.dataset.duration) || 3000;
  }

  get scrollDuration() {
    return Number(this.dataset.scrollDuration) || 1200;
  }

  connectedCallback() {
    this.slider = this.querySelector('#announcement-bar__slider');
    if (!this.slider) return;

    this.slides = Array.from(this.slider.querySelectorAll('.announcement'));
    if (this.slides.length === 0) return;

    this.currentIndex = 0; // Start at first slide
    this._setupObserver();
    this._setupAutoSlide();
    this._setupEventListeners();
  }

  disconnectedCallback() {
    this._stopAutoSlide();
    if (this.observer) this.observer.disconnect();
    document.removeEventListener('visibilitychange', this._visibilityHandler);
  }

  _setupObserver() {
    this.observer = new IntersectionObserver(entries => {
      let maxRatio = 0;
      let activeSlide = null;

      entries.forEach(entry => {
        if (entry.intersectionRatio > maxRatio) {
          maxRatio = entry.intersectionRatio;
          activeSlide = entry.target;
        }
      });

      if (activeSlide) {
        const newIndex = this.slides.indexOf(activeSlide);
        if (newIndex !== -1 && newIndex !== this.currentIndex) {
          this.currentIndex = newIndex;
          this._onSlideChange();
        }
      }
    }, {
      root: this.slider,
      threshold: [0.1, 0.5, 0.9]
    });

    this.slides.forEach(slide => this.observer.observe(slide));
  }

  _onSlideChange() {
    this._stopAutoSlide();
    // Wait for scroll animation to finish + small buffer
    setTimeout(() => this._startAutoSlide(), this.scrollDuration + 200);
  }

  _setupAutoSlide() { this._startAutoSlide(); }

  _startAutoSlide() {
    this._stopAutoSlide();
    this.autoSlideTimer = setInterval(() => this.goToNextSlide(), this.duration);
  }

  _stopAutoSlide() {
    if (this.autoSlideTimer) {
      clearInterval(this.autoSlideTimer);
      this.autoSlideTimer = null;
    }
  }

  goToNextSlide() {
    const nextIndex = (this.currentIndex + 1) % this.slides.length;
    this.scrollAnnouncementToView(nextIndex);
  }

  scrollAnnouncementToView(slideIndex, duration = this.scrollDuration) {
    if (this.isScrolling) return;
    const slide = this.slides[slideIndex];
    if (!slide) return;

    this.isScrolling = true;

    const originalSnap = getComputedStyle(this.slider).scrollSnapType;
    this.slider.style.scrollSnapType = 'none';

    const startTop = this.slider.scrollTop;
    // Calculate exact scrollTop to center the slide (matches CSS snap-center)
    const targetTop = slide.offsetTop - (this.slider.offsetHeight / 2) + (slide.offsetHeight / 2);
    const distance = targetTop - startTop;

    const startTime = performance.now();
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease-in-out cubic for smooth deceleration
      const eased = progress < 0.5 
        ? 4 * progress * progress * progress 
        : 1 - Math.pow(-2 * progress + 2, 3) / 2;
      
      this.slider.scrollTop = startTop + distance * eased;
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        // Restore original snap behavior
        this.slider.style.removeProperty('scroll-snap-type');
        this.isScrolling = false;
      }
    };
    requestAnimationFrame(animate);
  }

  _setupEventListeners() {
    const stop = () => this._stopAutoSlide();
    const start = () => this._startAutoSlide();

    this.addEventListener('mouseenter', stop);
    this.addEventListener('mouseleave', start);
    this.addEventListener('focus', stop);
    this.addEventListener('blur', start);

    const handleVisibility = () => {
      if (document.hidden) this._stopAutoSlide();
      else this._startAutoSlide();
    };
    this._visibilityHandler = handleVisibility;
    document.addEventListener('visibilitychange', handleVisibility);
  }
}

customElements.define('announcement-bar', AnnouncementBar);
